import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { CheckCircle2 } from "lucide-react";
import { API, LOGO_URL } from "@/constants";
import { formatApiErrorDetail } from "@/context/AuthContext";

const inputCls =
  "w-full rounded-lg border border-kera-line bg-white px-4 py-2.5 text-sm text-kera-ink outline-none focus:border-kera-gold";
const labelCls = "block font-mono text-[10px] uppercase tracking-[0.2em] text-white/50 mb-1.5";

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [form, setForm] = useState({ password: "", confirm: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) {
      setError("Passwords do not match");
      return;
    }
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: form.password });
      setDone(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-black text-white font-body kera-grain" data-testid="reset-password-page">
      <div className="px-4 sm:px-8 max-w-md mx-auto pt-20 pb-24">
        <div className="text-center mb-10">
          <Link to="/">
            <img src={LOGO_URL} alt="KERA" className="h-12 w-auto mx-auto" data-testid="reset-password-logo" />
          </Link>
          <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.25em] text-kera-gold">Trader Programme</p>
        </div>

        <div className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm p-7 sm:p-8">
          {!token ? (
            <div className="text-center" data-testid="reset-password-missing-token">
              <h1 className="font-display font-semibold text-2xl">Invalid reset link</h1>
              <p className="mt-3 text-sm text-white/60">This link is missing its token. Please request a new reset link from the login page.</p>
            </div>
          ) : done ? (
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="text-center" data-testid="reset-password-success">
              <CheckCircle2 className="mx-auto text-kera-gold" size={40} strokeWidth={1.5} />
              <h1 className="mt-5 font-display font-semibold text-2xl">Password updated</h1>
              <p className="mt-3 text-sm text-white/60">You can now sign in with your new password.</p>
              <Link to="/trade" className="mt-7 inline-block rounded-full bg-kera-gold text-black px-7 py-3 text-sm font-semibold hover:bg-kera-goldlight transition-colors" data-testid="reset-password-login-link">
                Go to Trade Login
              </Link>
            </motion.div>
          ) : (
            <form onSubmit={submit} data-testid="reset-password-form">
              <h1 className="font-display font-semibold text-2xl mb-2">Choose a new password</h1>
              <p className="text-sm text-white/60 mb-6">At least 8 characters.</p>
              <label className={labelCls}>New Password</label>
              <input type="password" required minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={`${inputCls} mb-5`} data-testid="reset-password-new" />
              <label className={labelCls}>Confirm New Password</label>
              <input type="password" required value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} className={inputCls} data-testid="reset-password-confirm" />
              {error && <p className="mt-4 text-sm text-red-400" data-testid="reset-password-error">{error}</p>}
              <button type="submit" disabled={loading} className="mt-6 w-full rounded-full bg-kera-gold text-black py-3.5 text-sm font-semibold tracking-wide hover:bg-kera-goldlight transition-colors disabled:opacity-50" data-testid="reset-password-submit">
                {loading ? "Updating…" : "Set New Password"}
              </button>
            </form>
          )}
        </div>

        <p className="text-center mt-8">
          <Link to="/" className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40 hover:text-kera-gold transition-colors" data-testid="reset-password-back">
            ← Back to site
          </Link>
        </p>
      </div>
    </main>
  );
}
